/**
 * The control assistant (Phase 4A, redesigned in 4B): a contrasting panel at the top of the control page where
 * the IT lead uploads evidence and talks to the assistant, and a draft assessment that opens below it with the
 * proposed status and maturity up front. It is the only way the control page sends a rating to National, and
 * nothing is submitted until the IT lead confirms. National IT and the Board see the firm's submissions, never
 * the IT lead's conversation.
 */
import { maturityLabel, resultLabel, SUBMISSION_STATUS } from '@grc/shared/vocabulary';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ApiError, useApiClient, useDownload } from '../api';
import { Card, formatDate, Status } from '../components';
import { AcceptanceProposal, type ProposedAcceptance } from './AcceptanceProposal';
import { PlanProposal, type ProposedPlan } from './PlanProposal';

export interface EvidenceFile {
  id: number;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  scanStatus: string;
  uploadedBy: string;
  uploadedAt: string;
}

export interface DoesNotSupportPoint {
  point: string;
  reason: string;
}

export interface Draft {
  proposedStatus: 'no_gap' | 'partial_gap' | 'gap' | 'out_of_scope';
  proposedMaturity: number;
  maturityReasoning: string;
  implementationSummary: string;
  criteria: Array<{ criterion: string; result: 'shown' | 'partly_shown' | 'not_shown'; evidenceFileIds: number[]; note: string }>;
  /** Absent on drafts recorded before DEC-74. */
  doesNotSupport?: DoesNotSupportPoint[];
  remainingGaps: string[];
  nextSteps: string[];
  citations: string[];
  promptInjectionNoticed: boolean;
}

interface Message {
  id: number;
  role: 'user' | 'assistant';
  kind: 'chat' | 'draft';
  content: string | Draft;
  createdAt: string;
}

export interface Submission {
  id: number;
  status: string;
  proposedStatus: string;
  proposedMaturity: number | null;
  implementation: string;
  submittedBy: string;
  submittedAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  reviewedStatus: string | null;
  reviewedMaturity: number | null;
}

export interface AssistantState {
  enabled: boolean;
  canUse: boolean;
  accepts: string;
  maxBytes: number;
  files: EvidenceFile[];
  submissions: Submission[];
  conversation: { sessionId: string; messages: Message[] } | null;
}

export type DraftRef = { id: number; value: Draft };

export const CRITERION_RESULT: Record<string, string> = { shown: 'Shown', partly_shown: 'Partly shown', not_shown: 'Not yet shown' };
const STATUS_TEXT = (k: string) => resultLabel(k);

const toBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });

/** The latest draft in a conversation, if any. */
export const latestDraft = (s: AssistantState | null): DraftRef | null => {
  const d = [...(s?.conversation?.messages ?? [])].reverse().find((m) => m.kind === 'draft');
  return d ? { id: d.id, value: d.content as Draft } : null;
};

/** Renders the assistant's text: paragraphs, numbered and bulleted lists, bold, and links. */
export function RichText({ text }: { text: string }) {
  const inline = (t: string): ReactNode[] =>
    t.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\(https?:\/\/[^)\s]+\)|https?:\/\/[^\s)]+)/g).map((part, i) => {
      if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={i}>{part.slice(2, -2)}</strong>;
      const md = part.match(/^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/);
      if (md) return <a key={i} href={md[2]} target="_blank" rel="noreferrer">{md[1]}</a>;
      if (/^https?:\/\//.test(part)) return <a key={i} href={part} target="_blank" rel="noreferrer">{part}</a>;
      return part;
    });
  const blocks = text.split(/\n{2,}/);
  return (
    <>
      {blocks.map((b, i) => {
        const lines = b.split('\n');
        if (lines.every((l) => /^\s*\d+[.)]\s/.test(l))) return <ol key={i}>{lines.map((l, j) => <li key={j}>{inline(l.replace(/^\s*\d+[.)]\s/, ''))}</li>)}</ol>;
        if (lines.every((l) => /^\s*[-*•]\s/.test(l))) return <ul key={i}>{lines.map((l, j) => <li key={j}>{inline(l.replace(/^\s*[-*•]\s/, ''))}</li>)}</ul>;
        if (/^#{1,4}\s/.test(lines[0]!)) return <h4 key={i}>{inline(lines[0]!.replace(/^#{1,4}\s/, ''))}</h4>;
        return <p key={i}>{lines.map((l, j) => <span key={j}>{inline(l)}{j < lines.length - 1 && <br />}</span>)}</p>;
      })}
    </>
  );
}

/**
 * A conversation in a fixed-height window that scrolls, so a long chat does not stretch the page
 * (Andres, 2026-09-24). It follows the newest message as the conversation grows.
 */
export function ChatLog({ count, children }: { count: number; children: ReactNode }) {
  const ref = useRef<HTMLOListElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [count]);
  return (
    <ol ref={ref} className="chat chat-scroll" aria-live="polite" tabIndex={0} aria-label="Conversation">
      {children}
    </ol>
  );
}

/** What the control assistant is, in the words an IT lead needs before they start (DEC-74). */
export const ASSISTANT_LEDE =
  'The control assistant helps you describe how this control works, note what could be stronger, and name anything that does not support the Fully in place status. You set the status on the draft and send it to National IT.';

const ASSISTANT_STEPS = [
  'Tell it how the control works',
  'Add evidence',
  'Send the assessment',
  'Set the status and send it to National IT',
] as const;

/** The first step that is still ahead: explain, add a file, draft, then set the status. */
function assistantStep(replied: boolean, fileCount: number, draftReady: boolean): number {
  if (!replied) return 0;
  if (fileCount === 0) return 1;
  if (!draftReady) return 2;
  return 3;
}

/** Points from the assistant's draft. On the draft the IT lead sees the heading. On review the card supplies it. */
export function DoesNotSupportList({ items, heading = true }: { items: DoesNotSupportPoint[] | undefined; heading?: boolean }) {
  if (!items?.length) return null;
  return (
    <section className="does-not-support" aria-labelledby={heading ? 'does-not-support-h' : undefined} aria-label={heading ? undefined : 'Does not support the Fully in place status'}>
      {heading && <h3 id="does-not-support-h">Does not support the Fully in place status</h3>}
      <ul>
        {items.map((item, i) => (
          <li key={i}>
            <strong>{item.point}</strong> {item.reason}
          </li>
        ))}
      </ul>
      {heading && <p className="small">You can still send Fully in place. National IT reads this with the evidence.</p>}
    </section>
  );
}

export function AssistantSide({ s, firmCode, controlId, reload, onDraft, onAcceptanceRecorded, onPlanAdded, activeAcceptanceId = null, draftReady = false }: {
  s: AssistantState;
  firmCode: string;
  controlId: string;
  reload: () => void;
  onDraft: (d: DraftRef) => void;
  /** A draft is open below, so the current step is setting the status and sending it. */
  draftReady?: boolean;
  /** Called after the IT lead records a proposed risk acceptance, so the control page shows it. */
  onAcceptanceRecorded?: () => void;
  /** Called after the IT lead adds a proposed action plan, so the control page shows it (CP-04). */
  onPlanAdded?: () => void;
  /** The control's accepted decision, which a proposed one recorded on the control replaces (CP-05). */
  activeAcceptanceId?: number | null;
}) {
  const call = useApiClient();
  const download = useDownload();
  const [params] = useSearchParams();
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<null | 'upload' | 'send' | 'draft' | 'plan' | 'acceptance'>(null);
  const [error, setError] = useState<string | null>(null);
  const ask = params.get('ask');
  const [message, setMessage] = useState(ask ?? '');
  // A question handed over from elsewhere (risk register, acceptance form) fills the message box, ready to send.
  const [seenAsk, setSeenAsk] = useState(ask);
  if (ask !== seenAsk) {
    setSeenAsk(ask);
    if (ask) setMessage(ask);
  }
  useEffect(() => {
    if (ask) document.getElementById('assistant-h')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [ask]);
  const [attach, setAttach] = useState<number[]>([]);
  const [plan, setPlan] = useState<ProposedPlan | null>(null);
  const [acceptance, setAcceptance] = useState<ProposedAcceptance | null>(null);
  const [local, setLocal] = useState<Message[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(s.conversation?.sessionId ?? null);
  const messages = [...(s.conversation?.messages ?? []), ...local].filter((m) => m.kind === 'chat');
  const base = `/firms/${firmCode}/controls/${controlId}`;

  const guard = async (what: typeof busy, fn: () => Promise<void>) => {
    setBusy(what);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const upload = (files: FileList | null) =>
    guard('upload', async () => {
      for (const f of Array.from(files ?? [])) {
        if (f.size > s.maxBytes) throw new ApiError(400, `${f.name} is larger than ${s.maxBytes / 1024 / 1024} MB.`);
        await call(`${base}/evidence-files`, { method: 'POST', body: { fileName: f.name, dataBase64: await toBase64(f) } });
      }
      if (fileInput.current) fileInput.current.value = '';
      reload();
    });

  const remove = (id: number) => guard('upload', async () => (await call(`/firms/${firmCode}/evidence-files/${id}`, { method: 'DELETE' }), reload()));

  const send = (e: FormEvent) => {
    e.preventDefault();
    const text = message.trim();
    if (!text) return;
    return guard('send', async () => {
      const now = new Date().toISOString();
      // An open proposal card stays while the conversation goes on; a new proposal replaces it (CP-07).
      const r = await call<{ sessionId: string; messageId: number; reply: string; proposePlan: ProposedPlan | null; proposeAcceptance?: ProposedAcceptance | null }>(`${base}/assistant/turn`, {
        method: 'POST',
        body: { message: text, sessionId, evidenceFileIds: attach },
      });
      setSessionId(r.sessionId);
      setLocal((l) => [...l, { id: -r.messageId, role: 'user', kind: 'chat', content: text, createdAt: now }, { id: r.messageId, role: 'assistant', kind: 'chat', content: r.reply, createdAt: now }]);
      if (r.proposePlan) setPlan(r.proposePlan);
      if (r.proposeAcceptance) setAcceptance(r.proposeAcceptance);
      setMessage('');
      setAttach([]);
    });
  };

  // "Track as an action plan": the assistant proposes a plan for the main unplanned gap in the conversation.
  const track = () =>
    guard('plan', async () => {
      const r = await call<{ sessionId: string; proposePlan: ProposedPlan }>(`${base}/assistant/plan`, { method: 'POST', body: { sessionId } });
      setSessionId(r.sessionId);
      setPlan(r.proposePlan);
    });

  // "Record a risk acceptance": the assistant proposes a record of the decision the firm made in the conversation.
  const recordAcceptance = () =>
    guard('acceptance', async () => {
      const r = await call<{ sessionId: string; proposeAcceptance: ProposedAcceptance }>(`${base}/assistant/acceptance`, { method: 'POST', body: { sessionId } });
      setSessionId(r.sessionId);
      setAcceptance(r.proposeAcceptance);
    });

  // Rows on the self-assessment worklist link here with #assistant; bring it into view once it is on the page.
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === '#assistant') document.getElementById('assistant')?.scrollIntoView();
  }, [hash]);

  // A risk acceptance the draft wanted to propose but the portal held back is said here, not lost (SFH-03).
  const [acceptanceHeld, setAcceptanceHeld] = useState(false);
  const makeDraft = () =>
    guard('draft', async () => {
      setAcceptanceHeld(false);
      const r = await call<{ sessionId: string; draftMessageId: number; draft: Draft; proposeAcceptance?: ProposedAcceptance | null; acceptanceHeld?: boolean }>(`${base}/assistant/draft`, { method: 'POST', body: { sessionId } });
      setSessionId(r.sessionId);
      onDraft({ id: r.draftMessageId, value: r.draft });
      if (r.proposeAcceptance) setAcceptance(r.proposeAcceptance);
      setAcceptanceHeld(Boolean(r.acceptanceHeld) && !r.proposeAcceptance);
    });
  // One proposal card at a time; the button for the other kind waits until it is closed (UX-03).
  const cardOpen = Boolean(plan || acceptance);

  return (
    <section className="assistant-side" aria-labelledby="assistant-h" id="assistant">
      <h2 id="assistant-h">Control assistant</h2>
      {/* The one explanation of the assistant (PL-18, UX-02, UX-10): the banner that sat above this panel is merged in. */}
      <p className="assistant-intro assistant-lede">{ASSISTANT_LEDE}</p>
      <p className="assistant-intro">
        It checks what you describe and the files you add against the CIS safeguard, Baker Tilly policy and current good practice, and explains how to put the
        control in place with your firm's tools. Every reply ends with the one next step. An action plan or a risk acceptance is suggested only when you ask, and
        nothing is recorded until you click. It advises; your firm decides.
      </p>
      <ol className="assistant-steps" aria-label="Steps">
        {ASSISTANT_STEPS.map((label, i) => (
          <li key={label} aria-current={i === assistantStep(messages.some((m) => m.role === 'assistant'), s.files.length, draftReady) ? 'step' : undefined}>
            {label}
          </li>
        ))}
      </ol>
      {!s.enabled ? (
        <p className="assistant-note">The control assistant is not switched on in this environment yet.</p>
      ) : (
        <div className="assistant-body">
          <div className="assistant-talk">
            <ChatLog count={messages.length + (busy === 'send' ? 1 : 0)}>
              {messages.length === 0 && (
                <li className="assistant-note">Start with how the control works today: which tool does it, which systems it covers, and how you check it.</li>
              )}
              {messages.map((m) => (
                <li key={m.id} className={`bubble ${m.role}`}>
                  <span className="who">{m.role === 'user' ? 'You' : 'Assistant'}</span>
                  {m.role === 'assistant' ? <RichText text={m.content as string} /> : <p className="pre">{m.content as string}</p>}
                </li>
              ))}
              {busy === 'send' && (
                <li className="bubble assistant" role="status">
                  <span className="who">Assistant</span>
                  <p>Checking your records, the policies and current guidance…</p>
                </li>
              )}
            </ChatLog>
            {acceptanceHeld && (
              <p className="assistant-note" role="status">
                The assistant held back a risk acceptance record: it needs more detail first. Tell it what the firm accepts and why, or use Record a risk acceptance.
              </p>
            )}
            {plan && <PlanProposal firmCode={firmCode} plan={plan} canAdd onDone={() => setPlan(null)} onChanged={onPlanAdded} />}
            {acceptance && (
              <AcceptanceProposal firmCode={firmCode} proposal={acceptance} canAdd={s.canUse} onDone={() => setAcceptance(null)} onRecorded={onAcceptanceRecorded} supersedes={activeAcceptanceId} />
            )}
            <form onSubmit={send} className="form">
              <label>
                Your message
                <textarea id="assistant-message" rows={4} maxLength={8000} value={message} onChange={(e) => setMessage(e.target.value)} disabled={busy !== null} />
              </label>
              {s.files.length > 0 && (
                <fieldset className="attach">
                  <legend className="small">Point it to files</legend>
                  {s.files.map((f) => (
                    <label key={f.id} className="check">
                      <input type="checkbox" checked={attach.includes(f.id)} onChange={(e) => setAttach((a) => (e.target.checked ? [...a, f.id] : a.filter((x) => x !== f.id)))} />
                      #{f.id} {f.fileName}
                    </label>
                  ))}
                </fieldset>
              )}
              <div className="actions">
                {messages.length === 0 ? (
                  <button className="pill" disabled={busy !== null || !message.trim()}>
                    {busy === 'send' ? 'Thinking…' : 'Start chat'}
                  </button>
                ) : (
                  <>
                    <button className="pill" disabled={busy !== null || !message.trim()}>
                      {busy === 'send' ? 'Thinking…' : 'Send'}
                    </button>
                    <button type="button" className="pill secondary-dark" disabled={busy !== null} onClick={makeDraft}>
                      {busy === 'draft' ? 'Drafting…' : 'Send the assessment'}
                    </button>
                  </>
                )}
              </div>
              {messages.some((m) => m.role === 'assistant') && (
                <p className="assistant-note">On the assessment, set the status and send it to National IT.</p>
              )}
            </form>
            {messages.some((m) => m.role === 'assistant') && !cardOpen && (
              <p className="proposal-actions small">
                If you need one,{' '}
                <button type="button" className="link track-plan" disabled={busy !== null} onClick={track}>
                  {busy === 'plan' ? 'Drafting a plan…' : 'Track a gap as an action plan'}
                </button>
                {' or '}
                <button type="button" className="link track-plan" disabled={busy !== null} onClick={recordAcceptance}>
                  {busy === 'acceptance' ? 'Drafting the record…' : 'Record a risk acceptance'}
                </button>
              </p>
            )}
          </div>

          <div className="assistant-evidence">
            <h3>Evidence files</h3>
            {s.files.length ? (
              <ul className="plain files">
                {s.files.map((f) => (
                  <li key={f.id}>
                    <button className="link" onClick={() => download(`/firms/${firmCode}/evidence-files/${f.id}/content`, f.fileName)}>
                      #{f.id} {f.fileName}
                    </button>{' '}
                    <span className="assistant-note">
                      {Math.ceil(f.sizeBytes / 1024)} KB, {formatDate(f.uploadedAt)}
                      {f.scanStatus === 'pending' && ', scanning'}
                      {f.scanStatus === 'malicious' && ', flagged by malware scanning'}
                    </span>
                    <button className="link" onClick={() => remove(f.id)} aria-label={`Remove ${f.fileName}`} disabled={busy !== null}>
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="assistant-note">No files yet. Screenshots, policies and configuration exports all help.</p>
            )}
            <label className="upload">
              Add files <span className="assistant-note">({s.accepts}, up to {s.maxBytes / 1024 / 1024} MB each)</span>
              <input ref={fileInput} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx,.txt,.csv,.md,.log" onChange={(e) => upload(e.target.files)} disabled={busy !== null} />
            </label>
            {busy === 'upload' && (
              <p role="status" className="assistant-note">
                Uploading…
              </p>
            )}
          </div>
        </div>
      )}
      {error && (
        <p role="alert" className="assistant-error">
          {error}
        </p>
      )}
    </section>
  );
}

/** The draft assessment, in clear sections, for the IT lead to check, edit and submit. */
export function DraftEditor({ draft, files, busy, onSubmit, onDiscard }: {
  draft: Draft;
  files: EvidenceFile[];
  busy: boolean;
  onSubmit: (body: { proposedStatus: string; proposedMaturity: number; implementation: string; remainingGaps: string[]; evidenceMapping: Draft['criteria'] }) => void;
  onDiscard: () => void;
}) {
  const [status, setStatus] = useState<string>(draft.proposedStatus);
  const [maturity, setMaturity] = useState(draft.proposedMaturity);
  const [implementation, setImplementation] = useState(draft.implementationSummary);
  const [gaps, setGaps] = useState(draft.remainingGaps.join('\n'));
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => {
    document.getElementById('draft-h')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  const name = (id: number) => files.find((f) => f.id === id)?.fileName ?? `file #${id}`;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit({ proposedStatus: status, proposedMaturity: maturity, implementation, remainingGaps: gaps.split('\n').map((g) => g.trim()).filter(Boolean), evidenceMapping: draft.criteria });
  };
  return (
    <section className="card draft" aria-labelledby="draft-h">
      <h2 id="draft-h">Draft assessment</h2>
      <p className="notice small">Drafted by the control assistant. Check every section and change anything that is not right before you send it.</p>
      {draft.promptInjectionNoticed && <p className="notice small">The assistant found text in the evidence or conversation that tried to instruct it. It ignored it; please check the files.</p>}
      <DoesNotSupportList items={draft.doesNotSupport} />
      <form onSubmit={submit} className="form">
        <h3>Status you are sending to National IT</h3>
        <p className="small muted">Set Fully in place, Partly in place, or Not yet in place. National IT reviews what you send.</p>
        <div className="row">
          <label>
            Status
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {(['no_gap', 'partial_gap', 'gap', 'out_of_scope'] as const).map((k) => (
                <option key={k} value={k}>
                  {STATUS_TEXT(k)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Maturity (0 to 5)
            <select value={maturity} onChange={(e) => setMaturity(Number(e.target.value))}>
              {[0, 1, 2, 3, 4, 5].map((l) => (
                <option key={l} value={l}>
                  {maturityLabel(l)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="small">
          The assistant recorded {STATUS_TEXT(draft.proposedStatus)}, maturity {maturityLabel(draft.proposedMaturity)}.
        </p>
        <p className="small">
          <strong>Why this level:</strong> {draft.maturityReasoning}
        </p>

        <h3>How the control is implemented</h3>
        <label>
          <span className="sr-only">How the control is implemented</span>
          <textarea rows={6} minLength={20} maxLength={8000} required value={implementation} onChange={(e) => setImplementation(e.target.value)} />
        </label>

        <h3>What the evidence shows</h3>
        <div className="table-wrap" tabIndex={0} role="region" aria-label="What the evidence shows">
          <table>
            <caption className="sr-only">Each requirement, the evidence for it, and the result</caption>
            <thead>
              <tr>
                <th scope="col">What the control asks for</th>
                <th scope="col">Evidence</th>
                <th scope="col">Result</th>
              </tr>
            </thead>
            <tbody>
              {draft.criteria.map((c, i) => (
                <tr key={i}>
                  <th scope="row">
                    {c.criterion}
                    {c.note && <div className="muted small">{c.note}</div>}
                  </th>
                  <td>{c.evidenceFileIds.length ? c.evidenceFileIds.map(name).join(', ') : 'None'}</td>
                  <td>
                    <span className={`chip crit-${c.result}`}>{CRITERION_RESULT[c.result]}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3>Still missing</h3>
        <label>
          <span className="sr-only">Still missing, one per line</span>
          <textarea rows={4} value={gaps} onChange={(e) => setGaps(e.target.value)} />
        </label>

        {draft.nextSteps.length > 0 && (
          <>
            <h3>Recommended next steps</h3>
            <ol className="next-steps">
              {draft.nextSteps.map((n, i) => (
                <li key={i}>
                  <RichText text={n} />
                </li>
              ))}
            </ol>
          </>
        )}

        {draft.citations.length > 0 && (
          <>
            <h3>Sources</h3>
            <ul className="sources small">
              {draft.citations.map((c, i) => (
                <li key={i}>
                  <RichText text={c} />
                </li>
              ))}
            </ul>
          </>
        )}

        <p className="draft-sending">
          You are sending: <strong>{STATUS_TEXT(status)}, maturity {maturityLabel(maturity)}</strong>
          {(status !== draft.proposedStatus || maturity !== draft.proposedMaturity) && <span className="muted small"> (changed from the assistant's proposal)</span>}
        </p>
        <label className="check">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          I have checked this assessment and it describes how our firm has implemented the control.
        </label>
        <div className="actions">
          <button className="pill" disabled={!confirmed || busy}>
            {busy ? 'Sending…' : 'Send to National IT'}
          </button>
          <button type="button" className="pill secondary-dark" onClick={onDiscard}>
            Close the draft
          </button>
        </div>
      </form>
    </section>
  );
}

export function Submissions({ list }: { list: Submission[] }) {
  const shown = list.filter((s) => s.status !== 'superseded');
  if (!shown.length) return null;
  return (
    <Card title="Sent to National IT for review">
      <ul className="plain decisions">
        {shown.map((s) => (
          <li key={s.id} className="decision">
            <p>
              <Status map={SUBMISSION_STATUS} value={s.status} />{' '}
              <span className="muted small">
                Sent {formatDate(s.submittedAt)} by {s.submittedBy}: {STATUS_TEXT(s.proposedStatus)}
                {s.proposedMaturity !== null && `, maturity ${maturityLabel(s.proposedMaturity)}`}
              </span>
            </p>
            {s.reviewedAt && (
              <p className="small">
                {s.status === 'accepted' ? 'Reviewed' : 'Returned'} by {s.reviewedBy} on {formatDate(s.reviewedAt)}
                {s.status === 'accepted' && s.reviewedStatus && `: ${STATUS_TEXT(s.reviewedStatus)}, maturity ${maturityLabel(s.reviewedMaturity)}`}
              </p>
            )}
            {s.reviewNote && <p className="pre">{s.reviewNote}</p>}
          </li>
        ))}
      </ul>
    </Card>
  );
}
